import { useTailwindColor } from '@common/hooks/useTailwindColors';
import { Button } from '@common/components/atoms/Button';
import { Input } from '@common/components/atoms/Input';
import { MultiSelect } from '@common/components/atoms/MultiSelect';
import { SelectDropdown } from '@common/components/atoms/Select/SelectDropdown';
import { Typography } from '@common/components/foundations/Typography';
import { Icon } from '@common/components/foundations/icons';
import { Modal } from '@common/components/molecules/Modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRoomPrices } from '@pages/Client/Calendar/hooks/useRoomPrices';
import { schema } from '@pages/Client/PricingStrategy/YieldingTags/formSchema';
import { useHotelDetails } from '@pages/Client/hooks/useHotelDetails';
import { useUpdatePricingSettings } from '@pages/Client/hooks/useUpdatePricingSettings';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { Group, Radio } from '@mantine/core';

interface SelectDropdown {
  value: number;
  label: string;
}

interface RoomTypeTag {
  tag_name: string;
  color: string;
  min_adjustment: number;
  max_adjustment: number;
  room_types: number[];
  speed: number;
  id: string;
}

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClick?: () => void;
  children?: React.ReactNode;
  aggressivenessData?: SelectDropdown[];
  roomTypesData?: SelectDropdown[];
  selectedTag?: RoomTypeTag;
};

export const EditTagModal = ({
  isOpen,
  onClose,
  aggressivenessData,
  roomTypesData,
  selectedTag
}: ModalProps) => {
  const { pricingSettingsQuery } = useRoomPrices();
  const { savePricingSettings } = useUpdatePricingSettings();
  const { hotelDetails } = useHotelDetails();
  const { t } = useTranslation();

  const gold = useTailwindColor('gold');
  const indigo = useTailwindColor('indigo');
  const orange = useTailwindColor('orange');
  const uiRed = useTailwindColor('uiRed');
  const darkGreen = useTailwindColor('darkGreen');
  const grey = useTailwindColor('grey');

  const ColorOptions = [
    { key: 'orange.6', value: orange },
    { key: 'indigo.6', value: indigo },
    { key: 'yellow.6', value: gold },
    { key: 'gray.6', value: grey },
    { key: 'red.6', value: uiRed },
    { key: 'green.9', value: darkGreen }
  ];

  const colorMap: { [key: string]: string } = {
    '#FFA500': '#FE764B',
    '#32CD32': '#D62057',
    '#FF69B4': '#949494',
    '#00BFFF': '#ECBA59',
    '#0000CD': '#5B48EE',
    '#006400': '#06815B',
    '#ECBA59': '#ECBA59',
    '#FE764B': '#FE764B',
    '#5B48EE': '#5B48EE',
    '#D62057': '#D62057',
    '#949494': '#949494',
    '#06815B': '#06815B'
  };

  const defaultValues = {
    tag_name: selectedTag?.tag_name || '',
    color: (selectedTag?.color && colorMap[selectedTag.color]) || '#FE764B',
    min_adjustment: Number(selectedTag?.min_adjustment) ?? -10,
    max_adjustment: Number(selectedTag?.max_adjustment) ?? 10,
    room_types: selectedTag?.room_types || [],
    speed: selectedTag?.speed || 1
  };

  const { control, reset, handleSubmit, watch, formState } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues
  });

  useEffect(() => {
    if (selectedTag) {
      reset({
        tag_name: selectedTag?.tag_name || '',
        color: (selectedTag?.color && colorMap[selectedTag.color]) || '#FE764B',
        min_adjustment: Number(selectedTag?.min_adjustment) ?? -10,
        max_adjustment: Number(selectedTag?.max_adjustment) ?? 10,
        room_types: selectedTag?.room_types || [],
        speed: selectedTag?.speed || 1
      });
    }
  }, [selectedTag]);

  const onSubmit = async () => {
    const { data: latestPricingSettings } = await pricingSettingsQuery.refetch();

    if (!latestPricingSettings) {
      return;
    }

    const { room_type_tags } = latestPricingSettings;

    if (room_type_tags && selectedTag && room_type_tags[selectedTag.id]) {
      const updatedRoomTypeTags = {
        ...room_type_tags,
        [selectedTag.id]: {
          ...watch(),
          id: selectedTag.id
        }
      };
      const settings = JSON.stringify({
        ...latestPricingSettings,
        room_type_tags: updatedRoomTypeTags
      });

      if (selectedTag) {
        await savePricingSettings(settings);
      } else {
        return;
      }
    }
    onClose();
    reset();
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      open={isOpen}
      showFooter={false}
      onClose={handleClose}
      size="custom"
      disableCloseOnOk={!formState.isValid}
    >
      <div className="min-w-[480px] max-w-[480px] rounded-xl bg-white">
        <Button
          type="button"
          icon
          className="absolute right-6 top-6 z-30 translate-x-[50%] translate-y-[-50%] cursor-pointer bg-lightGrey"
          onClick={handleClose}
        >
          <Icon.Clear className="h-5 w-5" />
        </Button>
        <div className="p-5">
          <Typography className="mb-8" element="h6" color="darkGrey" variant="h6">
            {t('Edit Yielding Tag')}
          </Typography>
          <div className="flex w-full flex-col gap-4 ">
            <div className="flex w-full flex-col gap-4">
              <Controller
                name="tag_name"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    name="subject"
                    background="grey"
                    type="text"
                    value={value}
                    label={`${t('Tag Name')}*`}
                    placeholder={`${t('Please enter tag name')}`}
                    onChange={onChange}
                    onClear={() => reset({ tag_name: '' })}
                    error={!!error}
                    helperMessage={
                      error && (
                        <div className="flex items-center gap-2 text-error">
                          {error ? <Icon.WarningOutline className="w-4" /> : ''}
                          <Typography element="span" color="error" variant="meta-2">
                            {error ? error.message?.toString() : ''}
                          </Typography>
                        </div>
                      )
                    }
                  />
                )}
              />
              <div>
                <Typography element="p" variant="meta-1" className="mb-1 text-grey">
                  {t(`${hotelDetails?.room_apartment_space_name} Type`)}*
                </Typography>
                <Controller
                  control={control}
                  name="room_types"
                  render={({ field: { value, name, onChange }, fieldState: { error } }) => (
                    <>
                      <MultiSelect
                        placeholder="Select Room Types"
                        name={name}
                        options={roomTypesData}
                        onChange={onChange}
                        value={value}
                        inputClassName="py-[13.5px]"
                        fullWidth
                      />
                      {error && (
                        <div className="flex items-center gap-2 text-error">
                          <Icon.WarningOutline className="w-4" />
                          <Typography element="span" color="error" variant="meta-2">
                            {error.message?.toString()}
                          </Typography>
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex w-full justify-between gap-4">
                <div className="w-1/2">
                  <Controller
                    name="min_adjustment"
                    control={control}
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <Input
                        name={name}
                        background="grey"
                        type="number"
                        value={value}
                        label={`${t('Min Adjustment')}*`}
                        placeholder={`${t('Please enter min adjustment')}`}
                        trailingAddon="%"
                        onChange={(e) => onChange(parseInt(e.target.value))}
                        onClear={() => reset({ min_adjustment: 0 })}
                        error={!!error}
                        helperMessage={
                          error && (
                            <div className="flex items-center gap-2 text-error">
                              {error ? <Icon.WarningOutline className="w-4" /> : ''}
                              <Typography element="span" color="error" variant="meta-2">
                                {error ? error.message?.toString() : ''}
                              </Typography>
                            </div>
                          )
                        }
                      />
                    )}
                  />
                </div>
                <div className="w-1/2">
                  <Controller
                    name="max_adjustment"
                    control={control}
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <Input
                        name={name}
                        background="grey"
                        type="number"
                        value={value}
                        label={`${t('Max Adjustment')}*`}
                        placeholder={`${t('Please enter max adjustment')}`}
                        trailingAddon="%"
                        onChange={onChange}
                        onClear={() => reset({ max_adjustment: 0 })}
                        error={!!error}
                        helperMessage={
                          error && (
                            <div className="flex items-center gap-2 text-error">
                              {error ? <Icon.WarningOutline className="w-4" /> : ''}
                              <Typography element="span" color="error" variant="meta-2">
                                {error ? error.message?.toString() : ''}
                              </Typography>
                            </div>
                          )
                        }
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex w-full flex-col gap-4 ">
                <div className="w-full">
                  <Typography element="p" variant="meta-1" className="mb-1 text-grey">
                    {t('Aggressiveness')}*
                  </Typography>
                  <Controller
                    control={control}
                    name="speed"
                    render={({ field: { value, name, onChange } }) => (
                      <SelectDropdown
                        fullWidth
                        placeholder={`${t('Select Aggressiveness')}`}
                        name={name}
                        key={name}
                        value={value || ''}
                        options={aggressivenessData}
                        onChange={(value) => {
                          onChange(value);
                        }}
                      />
                    )}
                  />
                </div>
                <div>
                  <Typography element="p" variant="meta-1" className="mb-1 text-grey">
                    {t('Colour')}*
                  </Typography>
                  <div className="w-full">
                    <div className="mx-auto w-full max-w-md">
                      <Controller
                        name="color"
                        control={control}
                        defaultValue={orange}
                        render={({ field: { onChange, value } }) => (
                          <Radio.Group value={value} onChange={onChange} name="colour">
                            <Group>
                              {ColorOptions.map((color) => (
                                <Radio
                                  key={color.key}
                                  value={color.value}
                                  size={'lg'}
                                  icon={CheckIcon}
                                  iconColor={'white'}
                                  color={color.key}
                                  variant={'filled'}
                                  style={{
                                    '--mantine-color-white': color.value
                                  }}
                                  className={'[&>*>*>*]:!border-transparent'}
                                />
                              ))}
                            </Group>
                          </Radio.Group>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex h-[70px] w-full items-center justify-between rounded-b-xl bg-lightGrey-light px-[24px]">
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              type="button"
              intent="text"
              onClick={() => onClose()}
              className="text-copyTextGrey"
            >
              {t('Cancel')}
            </Button>
            <Button type="button" intent="primary" onClick={handleSubmit(onSubmit)}>
              {t('Save')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

function CheckIcon(props: any) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx={12} cy={12} r={12} fill="#fff" opacity="0.2" />
      <path
        d="M7 13l3 3 7-7"
        stroke="#fff"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
